import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { Router } from '@angular/router';

export interface CalendarMilestone {
  _id: string;
  title: string;
  color: string;
  startDate: string | null;
  dueDate: string | null;
  status: string;
  projectId?: { name: string };
  taskCount: number;
  completedTaskCount: number;
  percentage: number;
}

@Component({
  selector: 'app-milestone-dot',
  standalone: true,
  imports: [CommonModule, TooltipModule],
  template: `
    <ng-container *ngIf="forDay.length > 0">
      <div class="ms-dot-wrap">
        <span
          *ngFor="let ms of forDay | slice:0:3"
          class="ms-dot"
          [style.background]="getGradient(ms.color)"
          [pTooltip]="ms.title + (ms.projectId?.name ? ' · ' + ms.projectId?.name : '') + ' — ' + ms.percentage + '% done (' + ms.completedTaskCount + '/' + ms.taskCount + ' tasks)'"
          tooltipPosition="bottom"
          (click)="navigateTo(ms); $event.stopPropagation()"
          style="cursor: pointer"
        >
          <svg class="ms-svg" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.25)" stroke-width="3"/>
            <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.9)" stroke-width="3"
              stroke-dasharray="87.96"
              [attr.stroke-dashoffset]="87.96 - (87.96 * ms.percentage / 100)"
              stroke-linecap="round"
              transform="rotate(-90 18 18)"/>
            <text x="18" y="18" text-anchor="middle" dominant-baseline="central"
              fill="white" font-size="9" font-weight="700" font-family="sans-serif"
            >{{ ms.percentage }}%</text>
          </svg>
        </span>
        <span
          *ngIf="forDay.length > 3"
          class="ms-dot ms-dot-extra"
          [pTooltip]="'+' + (forDay.length - 3) + ' more milestones'"
          tooltipPosition="bottom"
          (click)="$event.stopPropagation()"
        >+{{ forDay.length - 3 }}</span>
      </div>
    </ng-container>
  `,
  styles: [`
    @keyframes ms-shimmer {
      0%   { background-position: -300% center; }
      100% { background-position:  300% center; }
    }
    @keyframes ms-pulse-glow {
      0%, 100% { box-shadow: 0 2px 6px rgba(0,0,0,0.25); }
      50%       { box-shadow: 0 4px 18px rgba(0,0,0,0.45); }
    }
    .ms-dot-wrap {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
      gap: 4px;
      margin-top: 5px;
    }
    .ms-dot {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 38px;
      height: 38px;
      border-radius: 50%;
      cursor: default;
      flex-shrink: 0;
      background-size: 300% auto !important;
      animation: ms-shimmer 2.5s linear infinite, ms-pulse-glow 2s ease-in-out infinite;
      transition: transform 0.15s ease;
      &:hover { transform: scale(1.2); z-index: 10; }
      .ms-svg { width: 38px; height: 38px; display: block; border-radius: 50%; }
    }
    .ms-dot-extra {
      background: linear-gradient(135deg, #64748b 0%, #94a3b8 50%, #64748b 100%) !important;
      background-size: 300% auto !important;
      font-size: 9px;
      font-weight: 700;
      color: #fff;
    }
  `],
})
export class MilestoneDotComponent {
  @Input() allMilestones: CalendarMilestone[] = [];
  @Input() dayDate!: Date;

  constructor(private router: Router) {}

  navigateTo(ms: CalendarMilestone): void {
    if (!ms.projectId) return;
    this.router.navigate(['/admin/milestone-board'], {
      queryParams: {
        id: (ms as any).projectId?._id ?? (ms as any).projectId,
        name: ms.projectId.name,
        highlightMilestone: ms._id
      }
    });
  }

  get forDay(): CalendarMilestone[] {
    if (!this.dayDate || !this.allMilestones.length) return [];
    const day = new Date(this.dayDate);
    day.setHours(0, 0, 0, 0);
    return this.allMilestones.filter((m) => {
      const e = m.dueDate ? new Date(m.dueDate) : null;
      if (e) e.setHours(0, 0, 0, 0);
      return e ? day.getTime() === e.getTime() : false;
    });
  }

  getGradient(color: string): string {
    return `linear-gradient(135deg, ${color} 0%, ${color}cc 40%, ${color}ff 60%, ${color}cc 80%, ${color} 100%)`;
  }
}
